import { useState, useEffect } from 'react'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@services/api'

// Types
interface KPIData {
  label: string
  value: string
  change: string
  changeType: 'positive' | 'negative'
  bgColor: string
  borderColor: string
  textColor: string
}

interface ChartDataPoint {
  name?: string
  [key: string]: string | number | undefined
}

interface BranchPerformance {
  route: string
  sales: number
  collected: number
  outstanding: number
  collectionPercentage: number
}

interface StockValueItem {
  segment: string
  value: number
  percentage: number
}

// Mock Data - Fallback if API unavailable
const MOCK_REVENUE_DATA: ChartDataPoint[] = [
  { day: 'Day 1', revenue: 1.2, grossProfit: 0.27 },
  { day: 'Day 2', revenue: 1.5, grossProfit: 0.33 },
  { day: 'Day 3', revenue: 1.8, grossProfit: 0.40 },
  { day: 'Day 4', revenue: 1.6, grossProfit: 0.35 },
  { day: 'Day 5', revenue: 2.1, grossProfit: 0.47 },
  { day: 'Day 6', revenue: 1.9, grossProfit: 0.42 },
  { day: 'Day 7', revenue: 2.3, grossProfit: 0.51 },
  { day: 'Day 8', revenue: 2.0, grossProfit: 0.44 },
  { day: 'Day 9', revenue: 1.7, grossProfit: 0.37 },
  { day: 'Day 10', revenue: 2.2, grossProfit: 0.49 },
  { day: 'Day 11', revenue: 2.4, grossProfit: 0.53 },
  { day: 'Day 12', revenue: 2.1, grossProfit: 0.47 },
  { day: 'Day 13', revenue: 1.8, grossProfit: 0.40 },
  { day: 'Day 14', revenue: 2.5, grossProfit: 0.56 },
  { day: 'Day 15', revenue: 2.3, grossProfit: 0.51 },
  { day: 'Day 16', revenue: 2.0, grossProfit: 0.44 },
  { day: 'Day 17', revenue: 2.2, grossProfit: 0.49 },
  { day: 'Day 18', revenue: 2.6, grossProfit: 0.58 },
  { day: 'Day 19', revenue: 2.4, grossProfit: 0.53 },
  { day: 'Day 20', revenue: 2.1, grossProfit: 0.47 },
  { day: 'Day 21', revenue: 2.7, grossProfit: 0.60 },
  { day: 'Day 22', revenue: 2.3, grossProfit: 0.51 },
  { day: 'Day 23', revenue: 2.0, grossProfit: 0.44 },
  { day: 'Day 24', revenue: 2.5, grossProfit: 0.56 },
  { day: 'Day 25', revenue: 2.8, grossProfit: 0.62 },
  { day: 'Day 26', revenue: 2.4, grossProfit: 0.53 },
  { day: 'Day 27', revenue: 2.2, grossProfit: 0.49 },
  { day: 'Day 28', revenue: 2.6, grossProfit: 0.58 },
  { day: 'Day 29', revenue: 2.9, grossProfit: 0.64 },
  { day: 'Day 30', revenue: 1.86, grossProfit: 0.41 },
]

const MOCK_STOCK_DATA: ChartDataPoint[] = [
  { name: 'Raw Material', value: 3.1, percentage: 27 },
  { name: 'In Repacking', value: 0.8, percentage: 7 },
  { name: 'Finished Goods', value: 7.5, percentage: 66 },
]

const MOCK_CASH_CREDIT_DATA: ChartDataPoint[] = [
  { week: 'Week 1', cash: 8.5, credit: 6.2 },
  { week: 'Week 2', cash: 9.2, credit: 7.1 },
  { week: 'Week 3', cash: 10.1, credit: 6.8 },
  { week: 'Week 4', cash: 8.9, credit: 8.0 },
]

const MOCK_TOP_PRODUCTS: ChartDataPoint[] = [
  { name: 'Cardamom', revenue: 3.2 },
  { name: 'Toor Dal', revenue: 2.8 },
  { name: 'Rice', revenue: 2.5 },
  { name: 'Pepper', revenue: 2.1 },
  { name: 'Turmeric', revenue: 1.8 },
]

const MOCK_CREDIT_AGING: ChartDataPoint[] = [
  { name: '0-7 days', value: 4.2 },
  { name: '8-15 days', value: 2.6 },
  { name: '16-30 days', value: 1.5 },
  { name: '30+ days', value: 1.1 },
]

const MOCK_REPACKING_LOSS: ChartDataPoint[] = [
  { day: 'Day 1', loss: 120 },
  { day: 'Day 2', loss: 140 },
  { day: 'Day 3', loss: 95 },
  { day: 'Day 4', loss: 320 },
  { day: 'Day 5', loss: 180 },
  { day: 'Day 6', loss: 110 },
  { day: 'Day 7', loss: 280 },
]

const MOCK_BRANCH_PERFORMANCE: BranchPerformance[] = [
  {
    route: 'Palayamkottai',
    sales: 15.2,
    collected: 14.1,
    outstanding: 1.1,
    collectionPercentage: 92.7,
  },
  {
    route: 'Tirunelveli Town',
    sales: 12.8,
    collected: 10.2,
    outstanding: 2.6,
    collectionPercentage: 79.7,
  },
  {
    route: 'Nanguneri',
    sales: 10.5,
    collected: 9.8,
    outstanding: 0.7,
    collectionPercentage: 93.3,
  },
  {
    route: 'Cash & Carry Counter',
    sales: 12.2,
    collected: 12.2,
    outstanding: 0,
    collectionPercentage: 100,
  },
]

const MOCK_STOCK_BREAKDOWN: StockValueItem[] = [
  { segment: 'Raw Material', value: 3.1, percentage: 27 },
  { segment: 'In Repacking', value: 0.8, percentage: 7 },
  { segment: 'Finished Goods', value: 7.5, percentage: 66 },
]

// Colors
const COLORS = ['#1F4B44', '#C77D1F', '#3C7A4F', '#B3432C', '#FF8C42']
const CHART_COLORS = {
  primary: '#1F4B44',
  accent: '#C77D1F',
  success: '#3C7A4F',
  danger: '#B3432C',
}

// KPI Card Component
function KPICard({ data }: { data: KPIData }) {
  return (
    <div className={`card bg-gradient-to-br ${data.bgColor} border-l-4 ${data.borderColor}`}>
      <div className={`text-sm ${data.textColor} font-semibold uppercase tracking-wide`}>
        {data.label}
      </div>
      <div className={`text-3xl font-bold ${data.textColor} mt-2`}>
        {data.value}
      </div>
      <div
        className={`text-xs ${data.textColor} mt-1 ${
          data.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
        }`}
      >
        {data.changeType === 'positive' ? '↑' : '↓'} {data.change}
      </div>
    </div>
  )
}

// Collection % Badge Component
function CollectionBadge({ percentage }: { percentage: number }) {
  if (percentage > 85) {
    return <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded font-semibold">OK</span>
  } else if (percentage < 75) {
    return <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded font-semibold">LOSS</span>
  } else {
    return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded font-semibold">ALERT</span>
  }
}

// Main Analytics Dashboard Component
export function Analytics() {
  const [dateRange, setDateRange] = useState<'today' | 'thisMonth'>('today')
  const [analyticsData, setAnalyticsData] = useState<any>(null)

  // Fetch analytics data
  const { data: apiData, isLoading, error } = useQuery({
    queryKey: ['analytics-dashboard', dateRange],
    queryFn: async () => {
      try {
        const res = await apiClient.get('/analytics/dashboard', {
          params: { period: dateRange },
        })
        return res
      } catch (err) {
        console.warn('API failed, using mock data:', err)
        return null
      }
    },
  })

  // Use API data or fallback to mock data
  useEffect(() => {
    if (apiData) {
      setAnalyticsData(apiData)
    } else {
      setAnalyticsData(null)
    }
  }, [apiData])

  // Export to CSV
  const handleExportCSV = () => {
    const timestamp = new Date().toLocaleString()
    const csvContent = [
      ['FMCG Operations Analytics Dashboard', timestamp],
      [],
      ['KPI Metrics'],
      ['Metric', 'Value', 'Change'],
      ['Revenue', '₹1,86,000', '+12%'],
      ['Gross Profit', '₹41,200', '22.2%'],
      ['MTD Revenue', '₹42,70,000', '+8%'],
      ['Stock Value', '₹11,40,000', '-'],
      [],
      ['Branch Performance'],
      ['Route', 'Sales', 'Collected', 'Outstanding', 'Collection %'],
      ...MOCK_BRANCH_PERFORMANCE.map((b) => [
        b.route,
        `₹${b.sales}L`,
        `₹${b.collected}L`,
        `₹${b.outstanding}L`,
        `${b.collectionPercentage}%`,
      ]),
      [],
      ['Stock Value Breakdown'],
      ['Segment', 'Value', '% of Total'],
      ...MOCK_STOCK_BREAKDOWN.map((s) => [`₹${s.value}L`, `${s.percentage}%`]),
    ]
      .map((row) => row.join(','))
      .join('\n')

    const element = document.createElement('a')
    element.setAttribute('href', `data:text/csv;charset=utf-8,${encodeURIComponent(csvContent)}`)
    element.setAttribute('download', `analytics-${new Date().toISOString().split('T')[0]}.csv`)
    element.style.display = 'none'
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
  }

  const kpiCards: KPIData[] = [
    {
      label: "Today's Revenue",
      value: '₹1.86L',
      change: '+12% vs yesterday',
      changeType: 'positive',
      bgColor: 'from-blue-50 to-blue-100',
      borderColor: 'border-blue-500',
      textColor: 'text-blue-700',
    },
    {
      label: "Today's Gross Profit",
      value: '₹41,200',
      change: '22.2% margin',
      changeType: 'positive',
      bgColor: 'from-green-50 to-green-100',
      borderColor: 'border-green-500',
      textColor: 'text-green-700',
    },
    {
      label: 'Month Revenue MTD',
      value: '₹42.7L',
      change: '+8% vs last month',
      changeType: 'positive',
      bgColor: 'from-purple-50 to-purple-100',
      borderColor: 'border-purple-500',
      textColor: 'text-purple-700',
    },
    {
      label: 'Total Stock Value',
      value: '₹11.4L',
      change: 'All segments',
      changeType: 'positive',
      bgColor: 'from-orange-50 to-orange-100',
      borderColor: 'border-orange-500',
      textColor: 'text-orange-700',
    },
  ]

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8 text-gray-500">Loading analytics dashboard...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600 text-sm mt-1">
            Real-time insights into operations and performance
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex gap-2 bg-white rounded-lg p-1 border border-gray-200">
            <button
              onClick={() => setDateRange('today')}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                dateRange === 'today'
                  ? 'bg-primary-500 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Today
            </button>
            <button
              onClick={() => setDateRange('thisMonth')}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                dateRange === 'thisMonth'
                  ? 'bg-primary-500 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              This Month
            </button>
          </div>
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 bg-accent-500 text-white rounded text-sm font-medium hover:bg-accent-600 transition-colors"
          >
            Export to CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          Failed to load some analytics. Displaying cached data.
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((kpi, idx) => (
          <KPICard key={idx} data={kpi} />
        ))}
      </div>

      {/* Charts Section 1: Revenue & Gross Profit */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Revenue & Gross Profit (30 Days)</h2>
        </div>
        <div className="w-full h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={MOCK_REVENUE_DATA} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 12 }}
                stroke="#6b7280"
                interval={2}
              />
              <YAxis
                label={{ value: '₹ Lakhs', angle: -90, position: 'insideLeft' }}
                tick={{ fontSize: 12 }}
                stroke="#6b7280"
              />
              <Tooltip
                formatter={(value) => `₹${value}L`}
                contentStyle={{ backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb' }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke={CHART_COLORS.primary}
                dot={{ fill: CHART_COLORS.primary, r: 4 }}
                name="Revenue"
                strokeWidth={2}
              />
              <Line
                type="monotone"
                dataKey="grossProfit"
                stroke={CHART_COLORS.accent}
                dot={{ fill: CHART_COLORS.accent, r: 4 }}
                name="Gross Profit"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Stock Value by Segment */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Stock Value by Segment</h2>
          </div>
          <div className="w-full h-80 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={MOCK_STOCK_DATA}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percentage }) => `${name} (${percentage}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {MOCK_STOCK_DATA.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `₹${value}L`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Cash vs Credit Sales */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Cash vs Credit Sales (This Month)</h2>
          </div>
          <div className="w-full h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={MOCK_CASH_CREDIT_DATA} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="week" tick={{ fontSize: 12 }} stroke="#6b7280" />
                <YAxis
                  label={{ value: '₹ Lakhs', angle: -90, position: 'insideLeft' }}
                  tick={{ fontSize: 12 }}
                  stroke="#6b7280"
                />
                <Tooltip
                  formatter={(value) => `₹${value}L`}
                  contentStyle={{ backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb' }}
                />
                <Legend />
                <Bar dataKey="cash" stackId="a" fill={CHART_COLORS.success} name="Cash" />
                <Bar dataKey="credit" stackId="a" fill={CHART_COLORS.accent} name="Credit" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Products by Revenue */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Top Products by Revenue</h2>
          </div>
          <div className="w-full h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={MOCK_TOP_PRODUCTS}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{ fontSize: 12 }} stroke="#6b7280" />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} stroke="#6b7280" />
                <Tooltip
                  formatter={(value) => `₹${value}L`}
                  contentStyle={{ backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb' }}
                />
                <Bar dataKey="revenue" fill={CHART_COLORS.primary} name="Revenue" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Credit Aging */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Credit Aging Analysis</h2>
          </div>
          <div className="w-full h-80 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={MOCK_CREDIT_AGING}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ₹${value}L`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {MOCK_CREDIT_AGING.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `₹${value}L`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Repacking Loss Trend */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Repacking Loss Trend (7-Day Period)</h2>
        </div>
        <div className="w-full h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={MOCK_REPACKING_LOSS} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="day" tick={{ fontSize: 12 }} stroke="#6b7280" />
              <YAxis
                label={{ value: '₹ per day', angle: -90, position: 'insideLeft' }}
                tick={{ fontSize: 12 }}
                stroke="#6b7280"
              />
              <Tooltip
                formatter={(value) => `₹${value}`}
                contentStyle={{ backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb' }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="loss"
                stroke={CHART_COLORS.danger}
                dot={{ fill: CHART_COLORS.danger, r: 5 }}
                name="Loss Amount"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tables Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Branch Performance Table */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Branch Performance</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Route</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Sales</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Collected</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Outstanding</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Collection %</th>
                </tr>
              </thead>
              <tbody>
                {MOCK_BRANCH_PERFORMANCE.map((branch, idx) => (
                  <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-gray-900 font-medium">{branch.route}</td>
                    <td className="py-3 px-4 text-right text-gray-900">₹{branch.sales}L</td>
                    <td className="py-3 px-4 text-right text-gray-900">₹{branch.collected}L</td>
                    <td className="py-3 px-4 text-right text-gray-900">₹{branch.outstanding}L</td>
                    <td className="py-3 px-4 text-center">
                      <CollectionBadge percentage={branch.collectionPercentage} />
                      <div className="text-xs text-gray-500 mt-1">{branch.collectionPercentage}%</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Stock Value Breakdown Table */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Stock Value Breakdown</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Segment</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Value</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">% of Total</th>
                </tr>
              </thead>
              <tbody>
                {MOCK_STOCK_BREAKDOWN.map((item, idx) => (
                  <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-gray-900 font-medium">{item.segment}</td>
                    <td className="py-3 px-4 text-right text-gray-900">₹{item.value}L</td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary-500"
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                        <span className="text-gray-900 font-medium w-8">{item.percentage}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-50 border-t-2 border-gray-200 font-semibold">
                  <td className="py-3 px-4 text-gray-900">Total</td>
                  <td className="py-3 px-4 text-right text-gray-900">
                    ₹{MOCK_STOCK_BREAKDOWN.reduce((sum, item) => sum + item.value, 0)}L
                  </td>
                  <td className="py-3 px-4 text-right text-gray-900">100%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Footer Note */}
      <div className="text-xs text-gray-500 text-center py-4">
        Data refreshes every 5 minutes. Last updated: {new Date().toLocaleTimeString()}
      </div>
    </div>
  )
}
